<template>
  <div class="mc-textures-container">
    <div class="generator-layout">
      <!-- Left Column with Sidebar and Banner -->
      <div class="left-column">
        <!-- Left Sidebar -->
        <div class="left-sidebar">
          <div class="sidebar-header">
            <div class="tabs">
              <button class="tab-btn" :class="{ active: activeTab === 'texture' }" @click="activateTab('texture')">
               Minecraft
              </button>
              <button class="tab-btn" :class="{ active: activeTab === 'upload' }" @click="activateTab('upload')">
                Custom
              </button>
            </div>
            
            <div class="search-box" v-if="activeTab === 'texture'">
              <input 
                type="text" 
                id="textureSearch" 
                v-model="searchQuery" 
                placeholder="Search.." 
                class="search-input"
              >
              <div class="search-icon" @click="clearSearch">
                <img :src="searchIcon" alt="Search Icon" class="w-6 h-6 mr-2"/>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'texture'" class="texture-grid">
            <div 
              v-for="texture in filteredTextures" 
              :key="texture.file" 
              class="texture-item" 
              :class="{ selected: selectedTexture === texture.file }"
              @click="selectTexture(texture.file)"
              @mouseover="showTooltip($event, formatTextureName(texture.file))"
              @mousemove="updateTooltipPosition($event)"
              @mouseout="hideTooltip"
            >
              <img :src="getTextureUrl(texture.file)" :alt="texture.file" loading="lazy">
            </div>
          </div>

          <div v-if="activeTab === 'upload'" class="upload-area">
            <div 
              class="dropzone" 
              :class="{ dragover: isDragging }" 
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleDrop"
              @click="triggerFileInput"
            >
              <p>Drag and drop a texture here<br>or click to select</p>
              <input type="file" ref="fileInput" accept="image/*" style="display: none;" @change="handleFileSelect">
            </div>

            <div class="upload-msg">
              For best results, please use 1:1 square textures.
            </div>
          </div>

          <button v-if="activeTab === 'texture' && selectedTexture" class="open-texture-btn" @click="openDownloadModal">
            Download Selected Texture
          </button>
        </div>

        <!-- Banner below sidebar -->
        <div class="sidebar-banner">
          <a :href="mciconsUrl" target="_blank" @click="handleBannerClick">
            <img src="@/assets/banners/banner2.png" alt="Vault Banner" class="sidebar-banner-img">
          </a>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="main-content">
        <div class="preview-banner">
          <a :href="mciconsUrl" target="_blank" @click="handleBannerClick">
            <img src="@/assets/banners/banner-long.png" alt="Vault Banner" class="preview-banner-img">
          </a>
        </div>
        
        <div class="preview-container">
          <div id="preview" ref="preview"
            @dragover.prevent="previewDragOver($event)"
            @dragleave.prevent="previewDragLeave($event)" 
            @drop.prevent="handlePreviewDrop($event)"
            :class="{ 'dragover': previewIsDragging, 'has-texture': selectedTexture || uploadedImage }"
          >
            <div v-if="!selectedTexture && !uploadedImage" class="no-texture-message">
              Select a texture or upload your own custom image to start
            </div>
            <div v-if="previewIsDragging" class="preview-drop-overlay">
              <div class="drop-message">Drop texture here</div>
            </div>
          </div>
        </div>

        <div class="preview-msg">
            The preview may look different from final exported image based on your selected texture.
        </div>
        
        <div class="settings-panels">
          <!-- Output Settings Panel -->
          <div class="settings-panel">
            <h3 class="panel-title">Image Settings</h3>
            
            <div class="settings-content">
              <div class="setting-row">
                <label>Texture Scale</label>
                <div class="slider-container">
                  <input 
                    type="range" 
                    v-model.number="imageScale" 
                    min="100" 
                    max="2000" 
                    step="50"
                    class="range-slider"
                  >
                  <span class="slider-value">{{ imageScale }}%</span>
                </div>
              </div>

              <div class="setting-row">
                <label>Image Resolution</label>
                <div class="resolution-options">
                  <button 
                    v-for="res in resolutions" 
                    :key="res.value" 
                    :class="{ active: resolution === res.value }"
                    @click="resolution = res.value"
                    class="res-btn"
                  >
                    {{ res.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Export Settings Panel -->
          <div class="settings-panel">
            <h3 class="panel-title">Export Settings</h3>
            
            <div class="settings-content">
              <div class="setting-row">
                <label>Image Format</label>
                <div class="format-options">
                  <button 
                    v-for="format in formatOptions" 
                    :key="format.value"
                    :class="{ active: selectedFormat === format.value }"
                    @click="selectedFormat = format.value"
                    class="format-btn"
                  >
                    {{ format.label }}
                  </button>
                </div>
              </div>

              <div class="setting-row" v-if="selectedFormat === 'jpg'">
                <label>Quality (JPG Only)</label>
                <div class="slider-container quality-slider">
                  <input 
                    type="range" 
                    v-model.number="quality" 
                    min="1" 
                    max="100"
                    class="range-slider"
                  >
                  <span class="slider-value">{{ quality }}%</span>
                </div>
              </div>

              <div class="setting-row">
                <button class="download-btn" @click="downloadBackground" :disabled="isDownloading">
                  {{ isDownloading ? 'Processing...' : 'Download' }}
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    
    <!-- Global tooltip -->
    <div 
      v-show="tooltip.visible" 
      class="global-tooltip"
      :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
    >
      {{ tooltip.text }}
    </div>

    <!-- Wrap the download modal in a transition component -->
    <Transition name="fade">
      <div v-if="downloadModal.visible" class="download-modal-overlay" @click="closeDownloadModal">
        <div class="download-modal" @click.stop>
          <div class="download-modal-header">
            <h3>Download Texture</h3>
            <button class="modal-close-btn" @click="closeDownloadModal">×</button>
          </div>
          <div class="download-modal-content">
            <div class="modal-texture-preview">
              <img :src="getTextureUrl(selectedTexture)" alt="Selected Texture" class="modal-texture-img">
              <div class="texture-name">{{ formatTextureName(selectedTexture) }}</div>
            </div>
            <div class="download-options">
              <button class="download-option-btn" @click="downloadTexture('original')">
                Original: 16×16
              </button>
              <button class="download-option-btn" @click="downloadTexture(512)">
                512×512
              </button>
              <button class="download-option-btn" @click="downloadTexture(1024)">
                1024×1024
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { authenticatedGet } from '../utils/auth';
import searchIcon from '@/assets/icons/search-icon.svg';
const mciconsUrl = getCurrentInstance().appContext.config.globalProperties.$mciconsUrl;

const handleBannerClick = (event) => {
  if (Math.random() < 0.5) {
    event.preventDefault();
    window.open('https://app.ccleaf.com?ref=apollo', '_blank');
  }
};

// References
const preview = ref(null);
const fileInput = ref(null);

// Tooltip state
const tooltip = ref({
  visible: false,
  text: '',
  x: 0,
  y: 0
});

const showTooltip = (event, text) => {
  tooltip.value.text = text;
  updateTooltipPosition(event);
  tooltip.value.visible = true;
};

const updateTooltipPosition = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  tooltip.value.x = rect.left + (rect.width / 2);
  tooltip.value.y = rect.bottom + 5;
};

const hideTooltip = () => {
  tooltip.value.visible = false;
};

// State variables
const textures = ref([]);
const selectedTexture = ref(null);
const activeTab = ref('texture');
const uploadedImage = ref(null);
const isDragging = ref(false);
const previewIsDragging = ref(false);
const searchQuery = ref('');
const imageScale = ref(getStoredValue('imageScale', 300));
const resolution = ref(getStoredValue('resolution', '1920x1080'));
const quality = ref(getStoredValue('quality', 90));
const selectedFormat = ref(getStoredValue('selectedFormat', 'png'));
const isDownloading = ref(false);

// Local storage functions
function getStoredValue(key, defaultValue) {
  const storedValue = localStorage.getItem(`mcicons_${key}`);
  return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
}

function saveToLocalStorage(key, value) {
  localStorage.setItem(`mcicons_${key}`, JSON.stringify(value));
}

// Watch for changes and save to localStorage
watch(imageScale, (newValue) => {
  saveToLocalStorage('imageScale', newValue);
  updatePreview();
});

watch(resolution, (newValue) => {
  saveToLocalStorage('resolution', newValue);
});

watch(quality, (newValue) => {
  saveToLocalStorage('quality', newValue);
});

watch(selectedFormat, (newValue) => {
  saveToLocalStorage('selectedFormat', newValue);
});

// Resolution options
const resolutions = [
  { value: '854x480', label: '480p' },
  { value: '1280x720', label: '720p' },
  { value: '1920x1080', label: '1080p' },
  { value: '2560x1440', label: '2K' },
  { value: '3840x2160', label: '4K' },
  { value: '7680x4320', label: '8K' }
];

// Format options
const formatOptions = [
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'webp', label: 'WEBP' }
];

// Download modal state
const downloadModal = ref({
  visible: false
});

// Fetch textures from API
const fetchTextures = async () => {
  try {
    const response = await authenticatedGet('/api/textures');
    textures.value = response.data.files || [];
  } catch (error) {
    console.error('Error fetching textures:', error);
  }
};

// Filter textures based on search query
const filteredTextures = computed(() => {
  if (!searchQuery.value) return textures.value;
  const query = searchQuery.value.toLowerCase();
  return textures.value.filter(texture => 
    texture.file.toLowerCase().includes(query)
  );
});

// Get texture URL
const getTextureUrl = (filename) => {
  return `/textures/${filename}`;
};

// Select a texture
const selectTexture = (file) => {
  selectedTexture.value = file;
  uploadedImage.value = null;
  updatePreview();
};

// Switch between tabs
const activateTab = (tab) => {
  activeTab.value = tab;
};

// Trigger file input click
const triggerFileInput = () => {
  fileInput.value.click();
};

// Handle file selection
const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    handleImageUpload(file);
  }
};

// Handle file drop
const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file && file.type.startsWith('image/')) {
    handleImageUpload(file);
  }
};

// Handle image upload
const handleImageUpload = (file) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    uploadedImage.value = e.target.result;
    selectedTexture.value = null;
    updatePreview();
  };
  reader.readAsDataURL(file);
};

// Update the preview
const updatePreview = () => {
  if (!preview.value) return;
  
  const imageUrl = uploadedImage.value || (selectedTexture.value ? getTextureUrl(selectedTexture.value) : null);
  
  if (imageUrl) {
    // Always use 16 as the base size, then scale according to imageScale
    const pixelSize = Math.floor(imageScale.value / 100 * 16);
    
    // Set CSS custom properties for the preview
    preview.value.style.setProperty('--texture-image', `url('${imageUrl}')`);
    preview.value.style.setProperty('--texture-size', `${pixelSize}px`);
    preview.value.classList.add('has-texture');
  } else {
    preview.value.style.setProperty('--texture-image', 'none');
    preview.value.classList.remove('has-texture');
  }
};

// Format texture name for tooltip
const formatTextureName = (filename) => {
  // Remove file extension
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
  // Replace underscores with spaces
  const nameWithSpaces = nameWithoutExt.replace(/_/g, " ");
  // Capitalize each word
  return nameWithSpaces.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Generate background and download
const downloadBackground = () => {
  if (!selectedTexture.value && !uploadedImage.value) {
    alert('Please select a texture or upload an image first');
    return;
  }

  isDownloading.value = true;
  generateBackground().then(url => {
    const link = document.createElement('a');
    
    // Construct the filename based on the specified format
    const textureName = selectedTexture.value ? selectedTexture.value.split('/').pop().split('.')[0] : 'custom_texture';
    const resolutionText = resolution.value; // e.g., '1920x1080'
    const formatText = selectedFormat.value; // e.g., 'png'
    
    link.download = `${textureName}_${resolutionText}(mcicons.ccleaf.com).${formatText}`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    isDownloading.value = false;
  }).catch(error => {
    console.error('Error generating background:', error);
    isDownloading.value = false;
  });
};

// Generate background
const generateBackground = async () => {
  return new Promise((resolve) => {
    const [width, height] = resolution.value.split('x').map(Number);
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Ensure pixel-perfect rendering
    ctx.imageSmoothingEnabled = false;
    
    const img = new Image();
    img.onload = () => {
      // Now scale the original image according to the user's scale setting
      const scaledSize = Math.floor((imageScale.value / 100) * 16);
      
      // Create the pattern at the desired scale
      const patternCanvas = document.createElement('canvas');
      patternCanvas.width = scaledSize;
      patternCanvas.height = scaledSize;
      const patternCtx = patternCanvas.getContext('2d');
      patternCtx.imageSmoothingEnabled = false;
      
      // Draw the original image scaled to 16x16 for the pattern
      patternCtx.drawImage(img, 0, 0, img.width, img.height, 0, 0, scaledSize, scaledSize);
      
      // Create the pattern from the scaled image
      const pattern = ctx.createPattern(patternCanvas, 'repeat');
      
      // Apply the pattern
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, width, height);
      
      // Export in the selected format
      const mimeType = selectedFormat.value === 'jpg' 
        ? `image/jpeg`
        : `image/${selectedFormat.value}`;
      
      const qualityValue = selectedFormat.value === 'jpg' ? quality.value / 100 : undefined;
      resolve(canvas.toDataURL(mimeType, qualityValue));
    };
    
    img.src = uploadedImage.value || getTextureUrl(selectedTexture.value);
  });
};

// Watch for changes to update preview
watch(imageScale, updatePreview);

// Clear search input
const clearSearch = () => {
  searchQuery.value = '';
};

const openDownloadModal = () => {
  downloadModal.value.visible = true;
};

const closeDownloadModal = () => {
  downloadModal.value.visible = false;
};

const downloadTexture = async (size) => {
  try {
    if (!selectedTexture.value) return;
    
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      
      // Set canvas size based on selected option
      if (size === 'original') {
        // Use 16x16 as the "original" size for all textures
        canvas.width = 16;
        canvas.height = 16;
      } else {
        // Use selected size
        canvas.width = size;
        canvas.height = size;
      }
      
      const ctx = canvas.getContext('2d');
      
      // Use nearest-neighbor interpolation for pixelated look
      ctx.imageSmoothingEnabled = false;
      
      // Draw image scaled to canvas size
      ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
      
      // Create download link
      const link = document.createElement('a');
      const filename = selectedTexture.value.split('/').pop();
      const sizeText = size === 'original' ? '16x16' : `${size}x${size}`;
      
      link.download = `${filename.split('.')[0]}_${sizeText}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    
    img.src = getTextureUrl(selectedTexture.value);
  } catch (error) {
    console.error('Error downloading texture:', error);
  }
};

// Preview drag & drop handling
const previewDragOver = (event) => {
  event.preventDefault();
  event.stopPropagation();
  previewIsDragging.value = true;
  event.dataTransfer.dropEffect = 'copy';
};

const previewDragLeave = (event) => {
  event.preventDefault();
  
  // Check if the leave event is for the container itself, not its children
  if (event.target === preview.value) {
    previewIsDragging.value = false;
  }
  
  // Check if the pointer is outside the preview area
  const rect = preview.value.getBoundingClientRect();
  if (
    event.clientX < rect.left ||
    event.clientX >= rect.right ||
    event.clientY < rect.top ||
    event.clientY >= rect.bottom
  ) {
    previewIsDragging.value = false;
  }
};

const handlePreviewDrop = (event) => {
  event.preventDefault();
  event.stopPropagation();
  previewIsDragging.value = false;
  
  const file = event.dataTransfer.files[0];
  if (file && file.type.startsWith('image/')) {
    handleImageUpload(file);
    // Switch to custom tab
    activeTab.value = 'upload';
  }
};

onMounted(() => {
  fetchTextures();
});
</script>

<style scoped>
.mc-textures-container {
  color: #e0e0e0;
  height: 100%;
  background-color: #000000;
  position: relative; /* Important for absolute positioning of the tooltip */
}

.generator-layout {
  display: flex;
  gap: 1rem;
  min-height: 600px;
}

/* Left Column Layout */
.left-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 320px;
  min-width: 320px;
}

/* Left Sidebar */
.left-sidebar {
  width: 100%;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: fit-content;
}

.sidebar-banner {
  width: 100%;
  text-align: center;
}

.sidebar-banner-img {
  max-width: 100%;
  transition: transform 0.2s;
}

.sidebar-banner-img:hover {
  transform: scale(1.02);
}

.sidebar-header {
  padding: 0.5rem;
  border-bottom: 2px solid #333333;
}

.tabs {
  display: flex;
  margin-bottom: 0.5rem;
}

.tab-btn {
  flex: 1;
  background: transparent;
  color: #00e5ff;
  border: none;
  padding: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
  text-transform: capitalize;
}

.tab-btn.active {
  border-bottom: 2px solid #00e5ff;
}

.tab-btn:not(.active) {
  color: #c7c7c8;
}

.tab-icon {
  margin-right: 0.25rem;
}

.search-box {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 0.5rem;
  padding-right: 2rem;
  background: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  color: #ffffff;
  font-size: 0.9rem;
  outline: 0;
  transition: all 0.2s ease-in;
}

.search-input:hover{
  border: 2px solid #A4A7B7;
  background: #45484C;
}

.search-input:focus{
  border: 2px solid #4de8ff!important;
  background: #1a2d44!important;
}

.search-icon {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.texture-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding: 8px;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  align-content: start;
  justify-content: start;
}

/* Create a container for the texture item that doesn't get cut off */
.texture-item {
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 64px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #191919;
  position: relative;
  aspect-ratio: 1;
}

.texture-item:hover {
  transform: scale(1.05);
  z-index: 100;
  border: 2px solid #00e5ff;
  background-color: #1a2d44;
}

.texture-item.selected {
  border-color: #00e5ff;
  box-shadow: 0 0 10px rgba(57, 255, 20, 0.5);
  z-index: 5;
}

.texture-item img {
  max-width: 100%;
  max-height: 100%;
  width: 65%;
  height: 65%;
  object-fit: contain;
  object-position: center;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  transition: transform 0.2s ease;
}

.texture-item:hover img {
  transform: scale(1.1);
}

/* Global tooltip that won't get cut off */
.global-tooltip {
  position: fixed;
  transform: translateX(-50%);
  background: #000000;
  color: #ffffff;
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  white-space: nowrap;
  z-index: 9999;
  font-family: 'Roboto', sans-serif;
  border: 2px solid #333;
  pointer-events: none;
  text-transform: capitalize;
}

.upload-area {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dropzone {
  border: 2px dashed #00e5ff;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  border-radius: 20px;
  background: rgba(57, 255, 20, 0.05);
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: smaller;
}

.dropzone:hover, .dropzone.dragover {
  background: rgba(57, 255, 20, 0.1);
}

.upload-msg{
  font-size: x-small;
  text-align: center;
  padding-top: 8px;
}

.open-texture-btn {
  margin: 0.5rem;
  background: #2a2a2a;
  color: #00e5ff;
  border: 2px solid #00e5ff;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: x-small;
  text-transform: capitalize;
}

.open-texture-btn:hover {
  background: #333333;
  transform: translateY(-2px);
}

/* Main Content Area */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-banner {
  text-align: center;
}

.preview-banner-img {
  max-width: 100%;
  border-radius: 0;
  transition: transform 0.2s;
}

.preview-banner-img:hover{
  transform: scale(1.01);
}

.preview-container {
  flex: 1;
  border: 2px solid #00e5ff;
  overflow: hidden;
  min-height: 300px;
  position: relative; /* Ensure proper positioning */

  background: -webkit-linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), -webkit-linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), #1e1e1e;
  background: -moz-linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), -moz-linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), #1e1e1e;
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), linear-gradient(45deg, rgba(0, 0, 0, 0.0980392) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.0980392) 75%, rgba(0, 0, 0, 0.0980392) 0), #1e1e1e;
  background-repeat: repeat, repeat;
  background-position: 0px 0, 10px 10px;
  -webkit-transform-origin: 0 0 0;
  transform-origin: 0 0 0;
  -webkit-background-origin: padding-box, padding-box;
  background-origin: padding-box, padding-box;
  -webkit-background-clip: border-box, border-box;
  background-clip: border-box, border-box;
  -webkit-background-size: 20px 20px, 20px 20px;
  background-size: 20px 20px, 20px 20px;
  -webkit-box-shadow: none;
  box-shadow: none;
  text-shadow: none;
  -webkit-transition: none;
  -moz-transition: none;
  -o-transition: none;
  transition: none;
  -webkit-transform: scaleX(1) scaleY(1) scaleZ(1);
  transform: scaleX(1) scaleY(1) scaleZ(1);
}

#preview {
  width: 100%;
  height: 300px;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: var(--texture-size, 16px);
  background-image: var(--texture-image, none);
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  position: relative; /* For absolute positioning of the overlay */
}

/* Preview styling to show/hide message */
#preview.has-texture .no-texture-message {
  display: none;
}

.preview-msg{
  text-align: center;
  display: block;
  font-size: x-small;
}

.no-texture-message {
  text-align: center;
  padding: 1rem;
  color: #a0a0a0;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
}

.settings-panels {
  display: flex;
  gap: 1rem;
}

.settings-panel {
  flex: 1;
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  padding: 1rem;
}

.panel-title {
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #333333;
  font-family: 'Roboto', sans-serif;
  text-transform: capitalize;
}

.settings-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem 0;
}

.setting-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 40px;
}

/* Add vertical alignment to children inside the row */
.setting-row > * {
  display: flex;
  align-items: center;
}

.setting-row label {
  display: block;
  color: #c7c7c8;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.range-slider {
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: #333333;
  border-radius: 999px;
  outline: none;
  transition: background 0.25s ease;
}

.range-slider::-webkit-slider-runnable-track {
  height: 6px;
  background: #333333;
  border-radius: 999px;
  border: none;
}

.range-slider::-moz-range-track {
  height: 6px;
  background: #333333;
  border-radius: 999px;
  border: none;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  margin-top: -5px;
  border-radius: 50%;
  background: #00e5ff;
  cursor: pointer;
  box-shadow: 0 0 0 0 rgba(0, 229, 255, 0.3);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.25s ease,
              background 0.25s ease;
}

.range-slider::-webkit-slider-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 0 0 6px rgba(0, 229, 255, 0.18);
}

.range-slider:active::-webkit-slider-thumb {
  transform: scale(1.25);
  box-shadow: 0 0 0 8px rgba(0, 229, 255, 0.25);
}

.range-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #00e5ff;
  border: none;
  cursor: pointer;
  box-shadow: 0 0 0 0 rgba(0, 229, 255, 0.3);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.25s ease,
              background 0.25s ease;
}

.range-slider::-moz-range-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 0 0 6px rgba(0, 229, 255, 0.18);
}

.range-slider:active::-moz-range-thumb {
  transform: scale(1.25);
  box-shadow: 0 0 0 8px rgba(0, 229, 255, 0.25);
}

.slider-value {
  min-width: 40px;
  text-align: right;
  color: #00e5ff;
  font-size: 0.9rem;
  font-family: 'Roboto', sans-serif;
}

.resolution-options, .format-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  margin-top: 0.25rem;
}

.res-btn, .format-btn {
  background: #2a2a2a;
  color: #c7c7c8;
  border: 2px solid #333333;
  border-radius: 20px;
  padding: 0.4rem 0.25rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.res-btn:hover, .format-btn:hover {
  border-color: #00e5ff;
  background-color: #313131;
}

.res-btn.active, .format-btn.active {
  color: #00e5ff;
  border: 2px solid #00e5ff;
  font-weight: bold;
}

.download-btn {
  text-align: center;
  display: block;
  background: #00e5ff;
  color: #000000;
  font-weight: bold;
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
  text-transform: capitalize;
  width: 100%;
}

.download-btn:hover {
  background: #00b8d4;
  transform: translateY(-2px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .generator-layout {
    flex-direction: column;
  }
  
  .left-column {
    width: 100%;
    min-width: 100%;
  }
  
  .left-sidebar {
    width: 100%;
  }
  
  .texture-grid {
    grid-template-columns: repeat(5, 1fr);
    max-height: 200px;
  }
  
  .settings-panels {
    flex-direction: column;
  }
  
  .sidebar-banner {
    display: none; /* Hide the sidebar banner on mobile devices */
  }
}

.has-texture {
  /* Additional styling for when a texture is selected */
  background-repeat: repeat;
}

/* Download Modal */
.download-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.download-modal {
  background: #1e1e1e;
  border: 2px solid #303030;
  width: 90%;
  max-width: 500px;
  font-family: 'Roboto', sans-serif;
}

.download-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #333;
}

.download-modal-header h3 {
  margin: 0;
  text-transform: capitalize;
}

.modal-close-btn {
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
}

.download-modal-content {
  display: flex;
  padding: 1rem;
}

.modal-texture-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-texture-img {
  width: 128px;
  height: 128px;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.texture-name {
  margin-top: 0.5rem;
  color: #ffffff;
  text-align: center;
  font-size: 0.9rem;
  text-transform: capitalize;
}

.download-options {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.download-options p {
  color: #a0a0a0;
  margin: 0 0 0.5rem 0;
}

.download-option-btn {
  background: #2a2a2a;
  color: #ffffff;
  border: 1px solid #333;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-family: 'Roboto', sans-serif;
  text-align: left;
  transition: all 0.2s;
}

.download-option-btn:hover {
  background: #333333;
  border-color: #00e5ff;
}

@media (max-width: 500px) {
  .download-modal-content {
    flex-direction: column;
  }
  
  .modal-texture-preview, .download-options {
    width: 100%;
  }
}

/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.preview-drop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none; /* So it doesn't interfere with drag events */
}

.drop-message {
  color: #00e5ff;
  font-family: 'Roboto', sans-serif;
  font-size: 1.2rem;
  text-align: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.5);
}

#preview.dragover {
  background-color: rgba(57, 255, 20, 0.05);
}

.settings-panel .download-btn:disabled {
  background: #666;
  cursor: not-allowed;
  transform: none;
}
</style> 